/**
 * Noticing that a newer build is deployed, and moving onto it.
 *
 * A service worker makes the app load instantly and work offline by serving the
 * build it cached. That is also its failure mode: with nothing watching, an
 * installed PWA can keep serving a build from weeks ago and look perfectly
 * healthy doing it. Two independent signals are watched, because each is blind
 * where the other sees:
 *
 *   - `/version.json`, stamped with the deployed build id. The only signal that
 *     works when there is no service worker at all.
 *   - The registration's own `updatefound`, the only signal that knows the new
 *     bytes are downloaded, installed, and ready to take over.
 *
 * This module reports that an update is waiting and applies it when asked. WHEN
 * to ask is the visitor's business and lives in `domain/pwa/autoUpdatePolicy`
 * behind the `useAutomaticAppUpdate` hook.
 *
 * Registration is deliberately quiet: a worker that fails to register must never
 * break the page it exists to make more reliable.
 */

import { hasDeployedRuntimeVersionChanged } from '../../domain/pwa/runtimeVersion';
import { fetchDeployedRuntimeVersion } from '../../infrastructure/build/deployedVersionClient';
import { currentRuntimeVersion, publishAppUpdateStatus } from './appUpdateStatus';

const SERVICE_WORKER_URL = '/service-worker.js';
const SKIP_WAITING_MESSAGE = 'SKIP_WAITING';
const UPDATE_CHECK_INTERVAL_MS = 60 * 1000;
/**
 * Every cache this app writes is named for its build, behind this prefix. It has
 * to match what `assets/service-worker.js` opens: the prefix is what makes "drop
 * this app's caches" possible without touching one some other path on the origin
 * owns.
 */
const CACHE_NAME_PREFIX = 'legends-';

/**
 * The browser objects this module touches, as one replaceable seam.
 *
 * They arrive at registration rather than being reached for at call time because
 * none of them can be substituted through their global in jsdom:
 * `location.reload` cannot be spied on, and `caches` does not exist there.
 */
const browser = {
    navigatorObject: null,
    windowObject: null,
    documentObject: null,
    cacheStorage: null,
    fetchImpl: null,
};

const boundRegistrations = new WeakSet();

let activeRegistration = null;
let activeVersionCheck = null;
let hasBoundControllerChange = false;
let hasStartedBackgroundChecks = false;
let hasReloadedForControllerChange = false;

function getNavigator() {
    return browser.navigatorObject || (typeof navigator === 'undefined' ? null : navigator);
}

function getWindow() {
    return browser.windowObject || (typeof window === 'undefined' ? null : window);
}

function getDocument() {
    return browser.documentObject || (typeof document === 'undefined' ? null : document);
}

function getCacheStorage() {
    return browser.cacheStorage || (typeof caches === 'undefined' ? null : caches);
}

function reloadPage() {
    const windowObject = getWindow();

    if (windowObject && windowObject.location) {
        windowObject.location.reload();
    }
}

function requestDeployedRuntimeVersion() {
    if (activeVersionCheck) {
        return activeVersionCheck;
    }

    publishAppUpdateStatus({ isChecking: true, error: null });

    activeVersionCheck = fetchDeployedRuntimeVersion({
        url: currentRuntimeVersion.manifestUrl,
        fetchImpl: browser.fetchImpl || globalThis.fetch,
    })
        .then((latestVersion) => {
            publishAppUpdateStatus({
                isChecking: false,
                latestVersion,
                hasNewBuild: hasDeployedRuntimeVersionChanged(currentRuntimeVersion, latestVersion),
                error: null,
            });

            return latestVersion;
        })
        .catch((error) => {
            // Offline is the common case here and is not worth surfacing: the
            // next check is a minute away, and the cached build still works.
            publishAppUpdateStatus({
                isChecking: false,
                error: error && error.message ? error.message : 'Unable to check the deployed version.',
            });

            return null;
        })
        .finally(() => {
            activeVersionCheck = null;
        });

    return activeVersionCheck;
}

function requestRegistrationUpdate(registration) {
    if (!registration || typeof registration.update !== 'function') {
        return Promise.resolve(null);
    }

    // A failed update check is the same non-event as a failed version check.
    return Promise.resolve(registration.update()).catch(() => null);
}

function bindControllerChangeReload(navigatorObject) {
    if (hasBoundControllerChange) {
        return;
    }

    hasBoundControllerChange = true;

    // A page that loaded WITHOUT a controller gets one the moment the first
    // worker claims it. That is not an update — the page is already running the
    // code it just fetched — and reloading on it would make every first visit
    // load twice.
    const wasControlledAtStartup = Boolean(navigatorObject.serviceWorker.controller);

    navigatorObject.serviceWorker.addEventListener('controllerchange', () => {
        if (!wasControlledAtStartup || hasReloadedForControllerChange) {
            return;
        }

        hasReloadedForControllerChange = true;
        reloadPage();
    });
}

function bindRegistrationUpdates(registration, navigatorObject) {
    if (!registration || boundRegistrations.has(registration)) {
        return registration;
    }

    boundRegistrations.add(registration);

    if (registration.waiting) {
        publishAppUpdateStatus({ isUpdateReady: true, error: null });
    }

    registration.addEventListener('updatefound', () => {
        const installingWorker = registration.installing;

        if (!installingWorker) {
            return;
        }

        installingWorker.addEventListener('statechange', () => {
            if (installingWorker.state !== 'installed') {
                return;
            }

            // Installed with no controller is the FIRST worker, not an update:
            // there is no previous build for it to replace, and reporting one
            // would reload a page that is already newest.
            publishAppUpdateStatus({
                isUpdateReady: Boolean(navigatorObject.serviceWorker.controller),
                error: null,
            });
        });
    });

    return registration;
}

function startBackgroundUpdateChecks() {
    const windowObject = getWindow();
    const documentObject = getDocument();

    if (hasStartedBackgroundChecks || !windowObject) {
        return;
    }

    hasStartedBackgroundChecks = true;

    const checkForUpdates = () => {
        requestRegistrationUpdate(activeRegistration);
        requestDeployedRuntimeVersion();
    };

    windowObject.setInterval(checkForUpdates, UPDATE_CHECK_INTERVAL_MS);
    windowObject.addEventListener('focus', checkForUpdates);

    // The interval alone is not enough for an installed app: a backgrounded
    // page's timers are throttled to nothing, so a phone that spent the day in a
    // pocket would otherwise wait a full minute after being opened to notice.
    if (documentObject) {
        documentObject.addEventListener('visibilitychange', () => {
            if (documentObject.visibilityState === 'visible') {
                checkForUpdates();
            }
        });
    }
}

async function unregisterServiceWorkers(navigatorObject) {
    if (typeof navigatorObject.serviceWorker.getRegistrations !== 'function') {
        return;
    }

    const registrations = await navigatorObject.serviceWorker.getRegistrations();

    await Promise.all(registrations.map((registration) => (
        registration && typeof registration.unregister === 'function'
            ? registration.unregister().catch(() => false)
            : Promise.resolve(false)
    )));
}

async function clearAppCaches() {
    const cacheStorage = getCacheStorage();

    if (!cacheStorage || typeof cacheStorage.keys !== 'function') {
        return;
    }

    const cacheKeys = await cacheStorage.keys();

    await Promise.all(
        cacheKeys
            .filter((cacheKey) => typeof cacheKey === 'string' && cacheKey.startsWith(CACHE_NAME_PREFIX))
            .map((cacheKey) => cacheStorage.delete(cacheKey)),
    );
}

/**
 * Move onto the newest build, now.
 *
 * The happy path hands over to the waiting worker and lets `controllerchange` do
 * the reload, so the page comes back served by the NEW worker's cache. Reloading
 * directly would land on the old worker and serve the old build again — the
 * update would appear to fail, and then repeat forever.
 */
export async function applyAppUpdate() {
    publishAppUpdateStatus({ isApplying: true, error: null });

    const navigatorObject = getNavigator();

    if (!navigatorObject || !('serviceWorker' in navigatorObject)) {
        reloadPage();

        return true;
    }

    try {
        const registration = activeRegistration
            || await navigatorObject.serviceWorker.getRegistration(SERVICE_WORKER_URL)
            || await navigatorObject.serviceWorker.getRegistration();

        if (registration) {
            bindRegistrationUpdates(registration, navigatorObject);
            await requestRegistrationUpdate(registration);

            if (registration.waiting) {
                registration.waiting.postMessage(SKIP_WAITING_MESSAGE);

                return true;
            }
        }

        // No worker, or its new bytes are not installed yet. The manifest has
        // already said this build is stale, so the honest move is to drop the
        // worker and the cache underneath it and take the page back to the
        // network — that cache is precisely what would serve the old build again.
        await unregisterServiceWorkers(navigatorObject);
        await clearAppCaches();
        reloadPage();

        return true;
    } catch (error) {
        console.error('Applying the app update failed:', error);
        publishAppUpdateStatus({
            isApplying: false,
            error: error && error.message ? error.message : 'Unable to update the app right now.',
        });
        reloadPage();

        return false;
    }
}

/**
 * @param {object} [browserOverrides] Replacement browser objects, for tests.
 */
export async function registerAppServiceWorker(browserOverrides = {}) {
    Object.assign(browser, browserOverrides);

    const navigatorObject = getNavigator();

    startBackgroundUpdateChecks();
    requestDeployedRuntimeVersion();

    // No service worker is a supported state, not a broken one: the manifest
    // check above still notices a new deploy, and applying it is a plain reload.
    if (!navigatorObject || !('serviceWorker' in navigatorObject)) {
        return null;
    }

    publishAppUpdateStatus({ isSupported: true });
    bindControllerChangeReload(navigatorObject);

    try {
        const registration = await navigatorObject.serviceWorker.register(SERVICE_WORKER_URL);

        activeRegistration = registration;
        bindRegistrationUpdates(registration, navigatorObject);
        requestRegistrationUpdate(registration);

        return registration;
    } catch (error) {
        console.error('Service worker registration failed:', error);

        return null;
    }
}
